/**
 * Deposit-side endpoints: where to send funds, and the user's own
 * sender-UID record.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "./client";

export interface BybitDestination {
  ready: boolean;
  operator_uid: string | null;
  needs_sender_uid: boolean;
}

export interface TonDestination {
  ready: boolean;
  wallet_address: string | null;
  memo: string | null;
  needs_memo: boolean;
}

export interface PaymentDestinations {
  bybit: BybitDestination;
  ton: TonDestination;
}

export function usePaymentDestinations() {
  return useQuery<PaymentDestinations, ApiError>({
    queryKey: ["payment_destinations"],
    queryFn: () =>
      api.get<PaymentDestinations>("/web/api/profile/payment_destinations"),
    // Operator addresses don't change often, but the per-user memo
    // does (rotation). Refresh on focus so the user sees their new
    // memo without a page reload.
    staleTime: 30_000,
  });
}

export interface UpdateDepositAddressBody {
  bybit: string;
  current_password: string;
  code?: string;
}

export interface DepositAddressResponse {
  deposit_address: Record<string, unknown>;
}

export function useUpdateDepositAddress() {
  const qc = useQueryClient();
  return useMutation<DepositAddressResponse, ApiError, UpdateDepositAddressBody>({
    mutationFn: (body) =>
      api.patch<DepositAddressResponse>(
        "/web/api/profile/deposit_address",
        { body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export interface DepositMemoResponse {
  memo: string | null;
}

export interface GenerateDepositMemoBody {
  current_password: string;
  code?: string;
}

export function useDepositMemo() {
  return useQuery<DepositMemoResponse, ApiError>({
    queryKey: ["deposit_memo"],
    queryFn: () =>
      api.get<DepositMemoResponse>("/web/api/profile/deposit_memo"),
  });
}

export function useGenerateDepositMemo() {
  const qc = useQueryClient();
  return useMutation<DepositMemoResponse, ApiError, GenerateDepositMemoBody>({
    mutationFn: (body) =>
      api.post<DepositMemoResponse>("/web/api/profile/deposit_memo", { body }),
    onSuccess: (data) => {
      qc.setQueryData(["deposit_memo"], data);
    },
  });
}
